import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AlertService } from '@core/service/alert/alert.service';
import { NewsletterService } from '@core/service/newsletter/newsletter.service';

interface FooterLink {
  key: string;
  route: string;
}

interface FooterLinkSection {
  titleKey: string;
  links: FooterLink[];
}

const FOOTER_SECTIONS: FooterLinkSection[] = [
  {
    titleKey: 'footer.sections.howToBuy',
    links: [
      { key: 'footer.links.paymentMethods', route: '/payment-methods' },
      { key: 'footer.links.orderAndPickUp', route: '/order-and-pickup' },
      { key: 'footer.links.orderWithDelivery', route: '/order-with-delivery' },
      { key: 'footer.links.shoppingOverThePhone', route: '/shopping-by-phone' },
      { key: 'footer.links.returns', route: '/returns' },
    ],
  },
  {
    titleKey: 'footer.sections.help',
    links: [
      { key: 'footer.links.contact', route: '/contact' },
      { key: 'footer.links.onlineHelp', route: '/online-help' },
      { key: 'footer.links.ourCommitments', route: '/our-commitments' },
      { key: 'footer.links.giveFeedback', route: '/feedback' },
    ],
  },
  {
    titleKey: 'footer.sections.sourcingTools',
    links: [
      { key: 'footer.links.productSearch', route: '/product-search' },
      { key: 'footer.links.supplierSearch', route: '/supplier-search' },
      { key: 'footer.links.sourcingRequests', route: '/sourcing-requests' },
      { key: 'footer.links.customManufacturing', route: '/custom-manufacturing' },
      { key: 'footer.links.expressUaeStock', route: '/express-uae-stock' },
      { key: 'footer.links.sustainableSourcing', route: '/sustainable-sourcing' },
    ],
  },
  {
    titleKey: 'footer.sections.about',
    links: [
      { key: 'footer.links.aboutUs', route: '/about-us' },
      { key: 'footer.links.pressOffice', route: '/press' },
      { key: 'footer.links.forSuppliers', route: '/suppliers' },
      { key: 'footer.links.regulations', route: '/regulations' },
      { key: 'footer.links.privacyPolicy', route: '/privacy-policy' },
      { key: 'footer.links.cookies', route: '/cookies' },
      { key: 'footer.links.personalDataRequest', route: '/personal-data-request' },
    ],
  },
];

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslatePipe],
  templateUrl: './footer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent {
  private readonly _fb = inject(FormBuilder);
  private readonly _alert = inject(AlertService);
  private readonly _translate = inject(TranslateService);
  private readonly _newsletterService = inject(NewsletterService);

  readonly currentYear = new Date().getFullYear();
  readonly footerSections = FOOTER_SECTIONS;
  readonly submitting = signal(false);
  readonly consentShake = signal(false);
  private _shakeTimeout?: ReturnType<typeof setTimeout>;

  readonly newsletterForm = this._fb.group({
    email: ['', [Validators.required, Validators.email]],
    acceptTerms: [false, [Validators.requiredTrue]],
  });

  submitNewsletter(): void {
    this.newsletterForm.markAllAsTouched();
    if (this.submitting()) return;

    if (this.newsletterForm.invalid) {
      if (this.newsletterForm.get('acceptTerms')?.invalid) this._triggerConsentShake();
      return;
    }

    const email = this.newsletterForm.value.email!;
    this.submitting.set(true);

    this._newsletterService.subscribe(email).subscribe({
      next: () => {
        this.submitting.set(false);
        this._alert.success(this._translate.instant('footer.newsletter.success'));
        this.newsletterForm.reset({ email: '', acceptTerms: false });
      },
      error: () => {
        this.submitting.set(false);
        this._alert.error(this._translate.instant('footer.newsletter.error'));
      },
    });
  }

  private _triggerConsentShake(): void {
    clearTimeout(this._shakeTimeout);
    this.consentShake.set(false);
    queueMicrotask(() => {
      this.consentShake.set(true);
      this._shakeTimeout = setTimeout(() => this.consentShake.set(false), 500);
    });
  }
}
